/**
 * Favourites Store
 * User-pinned companies + people. Surfaced in the Sidebar under Database.
 * Capped at MAX_FAVOURITES so the sidebar list stays scannable.
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

export const MAX_FAVOURITES = 5;

export type FavouriteKind = 'company' | 'person';

export interface FavouriteItem {
  kind: FavouriteKind;
  id: string;
  name: string;
  /** Company-only: hosted logo URL for sidebar thumbnail. */
  logoUrl?: string | null;
  /** Company-only: domain used by CompanyLogo for logo.dev fallback. */
  domain?: string | null;
  /** Person-only: scraped photo URL for sidebar avatar. */
  photoUrl?: string | null;
  /** ISO timestamp — most-recent-first ordering in the sidebar. */
  addedAt: string;
}

interface FavouritesState {
  items: FavouriteItem[];
  /** Sidebar collapsed state (persisted). */
  collapsed: boolean;

  isFavourite: (kind: FavouriteKind, id: string) => boolean;
  /** Add if not present, remove if present. Returns `false` when adding would
   *  exceed MAX_FAVOURITES (the cap is a hard refusal — caller surfaces UI). */
  toggleFavourite: (item: Omit<FavouriteItem, 'addedAt'>) => boolean;
  /** Update the display metadata of an existing favourite in place (no-op if
   *  it isn't currently favourited). Used to re-hydrate names/logos captured
   *  before the backend `display_name` rollout — see `useRehydrateFavourites`.
   *  `addedAt` and ordering are preserved. */
  patchFavourite: (
    kind: FavouriteKind,
    id: string,
    patch: Partial<Pick<FavouriteItem, 'name' | 'logoUrl' | 'domain' | 'photoUrl'>>,
  ) => void;
  removeFavourite: (kind: FavouriteKind, id: string) => void;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
}

export const useFavouritesStore = create<FavouritesState>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        collapsed: false,

        isFavourite: (kind, id) =>
          get().items.some((f) => f.kind === kind && f.id === id),

        toggleFavourite: (input) => {
          const existing = get().items.find(
            (f) => f.kind === input.kind && f.id === input.id,
          );
          if (existing) {
            set((s) => ({
              items: s.items.filter(
                (f) => !(f.kind === input.kind && f.id === input.id),
              ),
            }));
            return true;
          }
          if (get().items.length >= MAX_FAVOURITES) return false;
          set((s) => ({
            items: [
              { ...input, addedAt: new Date().toISOString() },
              ...s.items,
            ],
          }));
          return true;
        },

        patchFavourite: (kind, id, patch) =>
          set((s) => {
            let changed = false;
            const items = s.items.map((f) => {
              if (f.kind !== kind || f.id !== id) return f;
              // Only rewrite keys that are actually present in `patch` and
              // differ — avoids gratuitous state churn (and re-persist) when
              // the backend already matches what we stored.
              const next = { ...f };
              for (const key of ['name', 'logoUrl', 'domain', 'photoUrl'] as const) {
                if (key in patch && patch[key] !== undefined && patch[key] !== f[key]) {
                  (next as Record<string, unknown>)[key] = patch[key];
                  changed = true;
                }
              }
              return changed ? next : f;
            });
            return changed ? { items } : s;
          }),

        removeFavourite: (kind, id) =>
          set((s) => ({
            items: s.items.filter((f) => !(f.kind === kind && f.id === id)),
          })),

        setCollapsed: (collapsed) => set({ collapsed }),
        toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
      }),
      { name: 'semaphoric-favourites' },
    ),
    { name: 'FavouritesStore' },
  ),
);

const favouritesSelector = (state: FavouritesState) => ({
  items: state.items,
  collapsed: state.collapsed,
  toggleCollapsed: state.toggleCollapsed,
  removeFavourite: state.removeFavourite,
});

export const useFavourites = () => useFavouritesStore(useShallow(favouritesSelector));
